import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@duck/ui'
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { useTar } from '@/modules'
import { trackFaq } from '@/modules/faqAnalytics'
import { StructuredDataScript } from '@/modules/seo'
import { ExternalURLs, ORGANIZATION_EMAIL } from '@/types/externalURLs'

const SITE_URL = ExternalURLs.Site

type EmployerFaqItem = {
  slug: string
  question: string
  answer: string[]
  bullets?: string[]
  cta?: boolean
}

type EmployerFaqSection = {
  id: string
  number: string
  title: string
  description: string
  items: EmployerFaqItem[]
}

function buildEmployerFaqJsonLd(sections: EmployerFaqSection[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: sections
      .flatMap((section) => section.items)
      .map((item) => ({
        '@type': 'Question',
        name: item.question,
        url: new URL(`/for-employers#${item.slug}`, SITE_URL).toString(),
        acceptedAnswer: {
          '@type': 'Answer',
          text: [...item.answer, ...(item.bullets ?? [])].join(' '),
        },
      })),
  }
}

function EmployerSectionControls({
  section,
  openItems,
  setOpenItems,
  t,
}: {
  section: EmployerFaqSection
  openItems: string[]
  setOpenItems: Dispatch<SetStateAction<string[]>>
  t: ReturnType<typeof useTar>['t']
}) {
  const openCount = section.items.filter((item) =>
    openItems.includes(item.slug)
  ).length
  const allOpen = openCount === section.items.length
  const allClosed = openCount === 0
  const controlClass =
    'inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-accent/40 hover:bg-accent/5 hover:text-accent disabled:pointer-events-none disabled:opacity-50'

  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button
        type="button"
        disabled={allOpen}
        className={controlClass}
        onClick={() =>
          setOpenItems((prev) =>
            Array.from(
              new Set([...prev, ...section.items.map((item) => item.slug)])
            )
          )
        }
      >
        <ArrowsPointingOutIcon className="h-4 w-4" />
        {t('faq.expandAll')}
      </button>
      <button
        type="button"
        disabled={allClosed}
        className={controlClass}
        onClick={() =>
          setOpenItems((prev) =>
            prev.filter(
              (slug) => !section.items.some((item) => item.slug === slug)
            )
          )
        }
      >
        <ArrowsPointingInIcon className="h-4 w-4" />
        {t('faq.collapseAll')}
      </button>
    </div>
  )
}

export function EmployersPageStructured() {
  const { t } = useTar()
  const faqSections = t('employerFaq.sections', {
    returnObjects: true,
  }) as unknown as EmployerFaqSection[]
  const [openItems, setOpenItems] = useState<string[]>([
    faqSections[0].items[0].slug,
  ])
  const openItemsRef = useRef(openItems)
  openItemsRef.current = openItems
  const structuredData = buildEmployerFaqJsonLd(faqSections)

  useEffect(() => {
    trackFaq('faq:item:open', {
      slug: faqSections[0].items[0].slug,
      sectionId: faqSections[0].id,
      trigger: 'initial',
    })
  }, [])

  return (
    <>
      <StructuredDataScript
        id="employers-faq-structured-data"
        data={structuredData}
      />
      <div className="flex flex-1 w-full justify-center">
        <main
          className="flex-1 min-w-0 max-w-6xl space-y-10 md:space-y-12 mt-4 md:mt-8 mb-16"
        >
          <section
            className="px-6 py-8 md:px-10 md:py-12"
            aria-labelledby="employers-heading"
          >
            <div className="max-w-3xl space-y-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-accent">
                {t('employers.eyebrow')}
              </p>
              <h1
                id="employers-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground"
              >
                {t('employers.title')}
                <span className="text-accent">.</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                {t('employers.introduction')}
              </p>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                {t('employers.subtitle')}
              </p>
            </div>
          </section>

          <section className="px-0" aria-label={t('employerFaq.ariaLabel')}>
            <div className="rounded-xl border border-border bg-card/50 p-5 md:p-8 space-y-10">
              {faqSections.map((section) => (
                <section
                  key={section.id}
                  id={section.id}
                  className="scroll-mt-24 grid gap-5 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1.5fr)]"
                  aria-labelledby={`${section.id}-heading`}
                >
                  <div className="space-y-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-accent">
                      {section.number}
                    </p>
                    <h2
                      id={`${section.id}-heading`}
                      className="text-2xl md:text-3xl font-bold text-foreground tracking-tight"
                    >
                      {section.title}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">
                      {section.description}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <EmployerSectionControls
                      section={section}
                      openItems={openItems}
                      setOpenItems={setOpenItems}
                      t={t}
                    />
                    <Accordion
                      type="multiple"
                      className="w-full"
                      value={section.items
                        .filter((item) => openItems.includes(item.slug))
                        .map((item) => item.slug)}
                      onValueChange={(next) => {
                        const previous = openItemsRef.current
                        next.forEach((slug) => {
                          if (!previous.includes(slug))
                            trackFaq('faq:item:open', {
                              slug,
                              sectionId: section.id,
                              trigger: 'user',
                            })
                        })
                        previous
                          .filter(
                            (slug) =>
                              section.items.some(
                                (item) => item.slug === slug
                              ) && !next.includes(slug)
                          )
                          .forEach((slug) =>
                            trackFaq('faq:item:close', {
                              slug,
                              sectionId: section.id,
                            })
                          )
                        setOpenItems((current) => [
                          ...current.filter(
                            (slug) =>
                              !section.items.some((item) => item.slug === slug)
                          ),
                          ...next,
                        ])
                      }}
                    >
                      {section.items.map((item) => (
                        <AccordionItem
                          key={item.slug}
                          value={item.slug}
                          id={item.slug}
                          className="scroll-mt-24"
                        >
                          <AccordionTrigger>
                            {item.question}
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-4">
                              {item.answer.map((paragraph) => (
                                <p key={paragraph}>{paragraph}</p>
                              ))}
                              {item.bullets ? (
                                <ul className="list-disc space-y-2 ms-6">
                                  {item.bullets.map((bullet) => (
                                    <li key={bullet}>{bullet}</li>
                                  ))}
                                </ul>
                              ) : null}
                              {item.cta && (
                                <a
                                  href={`mailto:${ORGANIZATION_EMAIL}`}
                                  onClick={() =>
                                    trackFaq('faq:contact:email_click', {
                                      slug: item.slug,
                                    })
                                  }
                                  className="inline-flex items-center justify-center rounded-lg bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground shadow-[0_0_24px_-8px_hsl(var(--color-accent)/0.55)] transition-all hover:-translate-y-0.5 hover:bg-accent/85"
                                >
                                  {t('employers.contactCta')}
                                </a>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </section>
              ))}
            </div>
          </section>
        </main>
      </div>
    </>
  )
}
